import React from 'react'
import { Media } from '@/payload-types'
import { Avatar } from '@/ui/atoms/Avatar'
import { useTranslations } from 'next-intl'
import { Icon } from '@/ui/atoms/Icon'

type AuthorTooltipContentProps = {
  name: string
  avatar?: Media | string | null
  description?: string | null
  shortDescription?: string | null
  linkedin?: string | null
  verified?: boolean,
  appearance?: 'verified' | 'default'
}

type AuthorData = {
  name: string
  avatar?: Media | string | null
  description?: string | null
  shortDescription?: string | null
  linkedin?: string | null
  verified?: boolean
  appearance?: 'verified' | 'default'
}

type MultipleAuthorsTooltipContentProps = {
  authors: AuthorData[]
}

export const AuthorTooltipContent: React.FC<AuthorTooltipContentProps> = ({
  name,
  avatar,
  description,
  shortDescription,
  linkedin,
  verified,
  appearance = 'verified',
}) => {
  const t = useTranslations('public')

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9">
          <Avatar userImage={avatar} initials={name.charAt(0)} size="medium" variant="circle" />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-label-S-semibold text-text-base">{name}</span>
          {verified && appearance === 'verified' && (
            <div className="flex items-center gap-1 text-xs text-text-subtle">
              <span className="text-body-XS-regular text-text-faded">{t('verified-author')}</span>
              <Icon name="check-circle" className="w-4 h-4 text-text-accent" />
            </div>
          )}
          {appearance === 'default' && shortDescription && (
            <p className="text-body-XS-regular text-text-faded">{shortDescription}</p>
          )}
        </div>
      </div>
      {description && (
        <p className="text-body-XS-regular text-text-base">{description}</p>
      )}
      <div className="flex items-center w-full">
        {linkedin && (
          <a
            href={linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-label-XS-semibold text-white p-2 mt-2 w-full text-center bg-blue-linkedin hover:bg-blue-700 rounded-sm flex items-center justify-center gap-2 leading-none"
          >
            <Icon name="linkedin-fill" className="w-4 h-4" />
            {t('view-linkedin')}
          </a>
        )}
      </div>
    </div>
  )
}

export const MultipleAuthorsTooltipContent: React.FC<MultipleAuthorsTooltipContentProps> = ({
  authors,
}) => {
  const t = useTranslations('public')

  return (
    <div className="flex flex-col gap-3 w-full max-w-sm">
      {authors.map((author, index) => {
        const appearance = author.appearance || 'verified'
        return (
          <div key={index} className="flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9">
                <Avatar
                  userImage={author.avatar}
                  initials={author.name.charAt(0)}
                  size="medium"
                  variant="circle"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-label-S-semibold text-text-base">{author.name}</span>
                {author.verified && appearance === 'verified' && (
                  <div className="flex items-center gap-1 text-xs text-text-subtle">
                    <span className="text-body-XS-regular text-text-faded">{t('verified-author')}</span>
                    <Icon name="check-circle" className="w-4 h-4 text-text-accent" />
                  </div>
                )}
                {appearance === 'default' && author.shortDescription && (
                  <p className="text-body-XS-regular text-text-faded">{author.shortDescription}</p>
                )}
              </div>
            </div>
            {author.description && (
              <p className="text-body-XS-regular text-text-base ml-12">
                {author.description}
              </p>
            )}
            {author.linkedin && (
              <a
                href={author.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-label-XS-semibold text-white p-2 ml-12 w-[calc(100%-3rem)] text-center bg-blue-linkedin hover:bg-blue-700 rounded-sm flex items-center justify-center gap-2 leading-none"
              >
                <Icon name="linkedin-fill" className="w-4 h-4" />
                {t('view-linkedin')}
              </a>
            )}
            {index < authors.length - 1 && (
              <div className="border-t border-border-subtle mt-2 pt-3" />
            )}
          </div>
        )
      })}
    </div>
  )
}
